import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { FileType } from '/@/modules/document/enums/FileType';
import { DocumentSubType } from '/@/modules/document/enums/DocumentSubType';
import { DocumentType } from '/@/modules/document/enums/DocumentType';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { extractLastPart } from '/@/modules/core/app/helpers/StringHelper';
import Lang from '/@/modules/core/app/services/Lang';
import { DocumentMeta } from '/@/modules/document/models/meta/DocumentMeta';
import dayjs from 'dayjs';
import { DateFormat } from '/@/modules/core/app/constants/date.constants';
import { getters } from '/@/modules/core/app/helpers/store';
import { fileSize } from '/@/modules/core/app/helpers/FileHelper';
import { DocumentOrigin } from '@/modules/document/enums/DocumentOrigin';
import { AutoUploadedDocumentSubType } from '@/modules/document/constants/AutoUploadedDocumentSubType';
import { DocumentBadgeType } from '@/modules/document/enums/DocumentBadgeType';
import { UserCollection } from '@/modules/user/models/UserCollection';
import type { DocumentOtherMeta } from '@/modules/document/models/meta/DocumentOtherMeta';
import type { DocumentAdministrationLetterMeta } from '@/modules/document/models/meta/DocumentAdministrationLetterMeta';
import type { DocumentClosingLetterMeta } from '@/modules/document/models/meta/DocumentClosingLetterMeta';
import type { DocumentDriversLicenseMeta } from '@/modules/document/models/meta/DocumentDriversLicenseMeta';
import type { DocumentGrantOfProbateMeta } from '@/modules/document/models/meta/DocumentGrantOfProbateMeta';
import type { DocumentMarriageContractMeta } from '@/modules/document/models/meta/DocumentMarriageContractMeta';
import type { DocumentWillMeta } from '@/modules/document/models/meta/DocumentWillMeta';
import { DocumentMetaType } from '@/modules/document/constants/DocumentMetaType';
import { Asset } from '@/modules/asset/models/Asset';
import { Liability } from '@/modules/liability/models/Liability';
import { EstateAccount } from '@/modules/estateaccount/models/EstateAccount';
import { User } from '@/modules/user/models/User';
import { Note } from '@/modules/note/models/Note';
import { EsignEnvelope } from '@/modules/esign/models/EsignEnvelope';
import { EstateTask } from '@/modules/estatetask/models/EstateTask';
import { ReadReceipt } from '@/modules/readreceipt/models/ReadReceipt';
import { Estate } from '@/modules/estate/models/Estate';
import { EstateTaxFiling } from '@/modules/estatetaxfiling/models/EstateTaxFiling';
import { LocaleType } from '@/modules/user/constants/LocaleType';
import type { EstateAccountRow } from '@/modules/estateaccount/models/EstateAccountRow';
import { DocumentExtractionCollection } from '@/modules/ai/models/DocumentExtractionCollection';
import { ExtractedContact } from '@/modules/ai/models/ExtractedContact';
import { DocumentBankStatementMeta } from '@/modules/document/models/meta/DocumentBankStatementMeta';

export class Document extends BaseEntityModel {
  id?: number;
  estateId?: number;
  modelType?: string | null;
  modelId?: number;
  name?: string;
  fileName?: string;
  fileExtension?: string;
  path?: string;
  url?: string;
  thumbnailUrl?: string;
  type?: string;
  subType?: string;
  origin?: string;
  editableType?: string;
  placeholderContext?: string;
  isRejected?: boolean;
  size?: string;
  uploadedByUserId?: number;
  hasOcr?: boolean;
  file?: File;
  metaType?: string | null;
  hasExtractedRecords?: boolean;
  meta?: Relationship<
    | DocumentOtherMeta
    | DocumentAdministrationLetterMeta
    | DocumentBankStatementMeta
    | DocumentClosingLetterMeta
    | DocumentDriversLicenseMeta
    | DocumentGrantOfProbateMeta
    | DocumentMarriageContractMeta
    | DocumentWillMeta
  > | null;
  asset?: Relationship<Asset> | null;
  liability?: Relationship<Liability> | null;
  estateAccount?: Relationship<EstateAccount> | null;
  user?: Relationship<User> | null;
  uploadedByUser?: Relationship<User> | null;
  lastEditedByUser?: Relationship<User> | null;
  notes?: Relationship<Note[]> | null;
  esignEnvelope?: Relationship<EsignEnvelope> | null;
  estateTasks?: Relationship<EstateTask[]> | null;
  readReceipts?: Relationship<ReadReceipt[]> | null;
  sharedUsers?: Relationship<User[]> | null;
  estate?: Relationship<Estate> | null;
  estateTaxFiling?: Relationship<EstateTaxFiling> | null;
  documentExtractions?: Relationship<DocumentExtractionCollection>;
  extractedContacts!: Relationship<ExtractedContact[] | null>;
  lastEditedByUserOnlyTrashed?: Relationship<User> | null;
  uploadedByUserOnlyTrashed?: Relationship<User> | null;
  producedAt?: string;

  constructor(model: Partial<Document> = {}) {
    super(model);

    this.id = model.id;
    this.estateId = model.estateId;
    this.modelType = model.modelType;
    this.modelId = model.modelId;
    this.name = model.name || Document.inferNameFromSubType(model.subType as string);
    this.file = model.file || (model.path ? File.createDummy(extractLastPart(model.path)) : null);
    this.fileName = model.fileName;
    this.fileExtension = model.fileExtension;
    this.path = model.path;
    this.url = model.url;
    this.thumbnailUrl = model.thumbnailUrl;
    this.type = model.type || Document.inferTypeFromSubType(model.subType as string);
    this.subType = model.subType;
    this.origin = model.origin ?? DocumentOrigin.MANUAL.key;
    this.editableType = model.editableType;
    this.metaType = model.metaType;
    this.hasExtractedRecords = model.hasExtractedRecords;
    this.isRejected = model.isRejected ?? false;
    this.size = model.size;
    this.uploadedByUserId = model.uploadedByUserId;
    this.hasOcr = model.hasOcr;
    this.producedAt = model.producedAt;

    this.asset = Asset.mapModelOrNull(model.asset);
    this.estate = Estate.mapModelOrNull(model.estate);
    this.estateAccount = EstateAccount.mapModelOrNull(model.estateAccount);
    this.liability = Liability.mapModelOrNull(model.liability);
    this.uploadedByUser = User.mapModelOrNull(model.uploadedByUser);
    this.lastEditedByUser = User.mapModelOrNull(model.lastEditedByUser);
    this.notes = Note.mapModelsOrNull(model.notes);
    this.esignEnvelope = EsignEnvelope.mapModelOrNull(model.esignEnvelope);
    this.estateTaxFiling = EstateTaxFiling.mapModelOrNull(model.estateTaxFiling);
    this.meta = DocumentMeta.factory(model);
    this.estateTasks = EstateTask.mapModelsOrNull(model.estateTasks);
    this.readReceipts = ReadReceipt.mapModels(model.readReceipts);
    this.sharedUsers = User.mapModels(model.sharedUsers);
    this.documentExtractions = DocumentExtractionCollection.mapModel(model.documentExtractions);
    this.extractedContacts = ExtractedContact.mapModelsOrNull(model.extractedContacts);
    this.lastEditedByUserOnlyTrashed = User.mapModelOrNull(model.lastEditedByUserOnlyTrashed);
    this.uploadedByUserOnlyTrashed = User.mapModelOrNull(model.uploadedByUserOnlyTrashed);
  }

  getModelName(): ModelType {
    return ModelType.DOCUMENT;
  }

  getUnreadBadgeType(userId: number): string | null {
    
    const unreadReceipt: ReadReceipt | undefined = ReadReceipt.findUnreadForUser(
      this.readReceipts,
      userId
    );

    if (!unreadReceipt) {
      return null;
    }

    return DocumentBadgeType.fromReadReceipt(unreadReceipt);
  }

  setMeta(meta: string): void {
    this.meta = DocumentMeta.factory(this, meta);
  }

  setMetaType(): this {
    this.metaType = DocumentMeta.getMetaType(this);
    this.meta = DocumentMeta.factory(this);

    return this;
  }

  get fileType(): string | undefined {
    return FileType.getByKey(this.fileExtension || this.file?.extension)?.key;
  }

  get fileTypeIcon(): string | undefined {
    return FileType.getByKey(this.fileExtension || this.file?.extension)?.icon;
  }

  get formattedSize(): string | undefined {
    return fileSize(this.size as number);
  }

  get formattedFileMeta(): string {
    const extension: string = this.fileExtension?.toUpperCase() ?? '';

    return this.size ? `${extension} - ${this.formattedSize}` : extension;
  }

  get formattedProducedAt(): string {
    return dayjs(this.producedAt).format(DateFormat.FULL_MONTH_DAY_YEAR);
  }

  get subtypeName(): string | undefined {
    return DocumentSubType.getByKey(this.subType)?.value;
  }

  get hasThumbnailPreview(): boolean {
    return (
      FileType.supportsThumbnailPreview(this.fileExtension || this.file?.extension) &&
      !!this.thumbnailSource
    );
  }

  get isPdf(): boolean {
    return (this.fileExtension || this.file?.extension) === FileType.PDF.key;
  }

  get isImage(): boolean {
    return FileType.getStandardImageAcceptTypes().includes(
      this.fileExtension || this.file?.extension || ''
    );
  }

  get isDocFileExtension(): boolean {
    return [FileType.DOC.key, FileType.DOCX.key].includes(this.fileExtension);
  }

  get canOpenUrlPreview(): boolean {
    if (!this.url) {
      return false;
    }

    if (this.isEditableExport) {
      return false;
    }

    if (this.isDocFileExtension) {
      return false;
    }

    const rawExtension: string | undefined = this.fileExtension || this.file?.extension;
    if (!rawExtension) {
      return false;
    }

    const extensionKey: string = rawExtension.startsWith('.')
      ? rawExtension.slice(1)
      : rawExtension;

    return FileType.supportsThumbnailPreview(extensionKey.toLowerCase());
  }

  get thumbnailSource(): string | undefined {
    return this.isPdf ? this.thumbnailUrl : this.url;
  }

  get nameWithExtension(): string {
    return `${this.name}.${this.fileExtension}`;
  }

  get localizedName(): string | undefined {
    if (this.modelType === ModelType.WILL && this.subType === DocumentSubType.ESTATE_WILL.key) {
      const fullName: string = getters.auth.getCurrentEstate()?.contact.fullName;
      const date: string = dayjs(this.createdAt)
        .locale(getters.auth.getUser()?.locale ?? LocaleType.ENGLISH)
        .format('LL');

      return `${Lang.t(this.modelType)} - ${fullName} - ${date}`;
    }

    return this.name;
  }

  static inferNameFromSubType(type: string): string {
    const subType: DocumentSubType | undefined = DocumentSubType.getByKey(type);
    return subType ? subType.value : Lang.t('document.estate-document');
  }

  static inferTypeFromSubType(subType: string): string {
    return subType ? DocumentType.findValueFromChildTypeKey(subType)?.key : '';
  }

  get hasFileChanged(): boolean {
    return !!this.file && this.file.size !== 0;
  }

  get isEstateType(): boolean {
    return this.type === DocumentType.ESTATE.key;
  }

  get isAssetType(): boolean {
    return this.type === DocumentType.ASSET.key;
  }

  get isEstateAccountType(): boolean {
    return this.type === DocumentType.IDENTIFICATION.key;
  }

  get isLiabilityType(): boolean {
    return this.type === DocumentType.LIABILITY.key;
  }

  get isOthersType(): boolean {
    return this.type === DocumentType.OTHERS.key;
  }

  get isAutoUploadedGuide(): boolean {
    const autoUploadedSubTypes: string[] = Object.values(AutoUploadedDocumentSubType);
    return !!this.subType && autoUploadedSubTypes.includes(this.subType);
  }

  get isInternalType(): boolean {
    return this.type === DocumentType.INTERNAL_DOCUMENT.key;
  }

  get showOtherType(): boolean {
    return (
      (this.hasOtherSubType() && !!this.meta) ||
      this.metaType === DocumentMetaType.DOCUMENT_OTHER_META
    );
  }

  setModelType(type: string): void {
    if (type === DocumentType.ASSET.key) {
      this.modelType = ModelType.ASSET;
    } else if (type === DocumentType.IDENTIFICATION.key) {
      this.modelType = ModelType.ESTATE_ACCOUNT;
    } else if (type === DocumentType.LIABILITY.key) {
      this.modelType = ModelType.LIABILITY;
    } else {
      this.modelType = null;
    }
  }

  get hasDisplayableAssignedToModel(): boolean {
    const displayableModels: ModelType[] = [
      ModelType.ASSET,
      ModelType.LIABILITY,
      ModelType.ESTATE_ACCOUNT
    ];

    return displayableModels.includes(this.modelType as ModelType) && !!this.modelId;
  }

  hasOtherSubType(): boolean {
    return (
      this.subType === DocumentSubType.TAX_OTHERS.key ||
      this.subType === DocumentSubType.ASSET_OTHERS.key ||
      this.subType === DocumentSubType.LIABILITY_OTHERS.key ||
      this.type === DocumentType.OTHERS.key
    );
  }

  get typeEnum(): DocumentType | undefined {
    return DocumentType.getByKey(this.type as string);
  }

  get displayType(): string | undefined {
    return this.typeEnum?.value;
  }

  get displayColor(): string | undefined {
    return this.typeEnum?.color;
  }

  get firstNote(): Note | undefined {
    return this.notes?.[0];
  }

  get hasEsignEnvelope(): boolean {
    return !!this.esignEnvelope;
  }

  setName(name: string, relatedEntityName?: string): void {
    this.name = relatedEntityName ? `${name} - ${relatedEntityName}` : name;
  }

  get isEditable(): boolean {
    switch (this.subType) {
      case DocumentSubType.ESTATE_PROBATE_DE_172.key:
      case DocumentSubType.ESTATE_PROBATE_DE_174.key:
        return false;
      default:
        return true;
    }
  }

  get isEditableExport(): boolean {
    return !!this.editableType;
  }

  get isEdited(): boolean {
    return DocumentSubType.isEditedSubType(this.subType);
  }

  get isTrueCertifiedCopy(): boolean {
    return DocumentSubType.isTrueCertifiedCopySubType(this.subType);
  }

  get isSystemGenerated(): boolean {
    return this.origin !== DocumentOrigin.MANUAL.key;
  }

  get isClaraAvailable(): boolean {
    return DocumentSubType.isClaraAvailable(this.subType);
  }

  isClaraAvailableFor(permission?: { canAccessConquestExtraction?: boolean }): boolean {
    return DocumentSubType.isClaraAvailableFor(this.subType, permission);
  }

  get displayableSharedUsers(): User[] | undefined {
    return new UserCollection(this.sharedUsers).nonStaffProfessionalUsers;
  }

  setFileName(name?: string, linkToEntity?: Asset | Liability | EstateAccountRow): void {
    if (name) {
      this.name = name;
      return;
    }

    if (this.isInternalType) {
      return;
    }

    const inferredName: string = Document.inferNameFromSubType(this.subType as string);

    if (linkToEntity) {
      this.name = `${inferredName} - ${this.getLinkedEntityName(linkToEntity)}`;
      return;
    }

    this.name = inferredName;
  }

  getLinkedEntityName(entity?: Asset | Liability | EstateAccountRow): string {
    switch (this.modelType) {
      case ModelType.ASSET:
        return this.asset?.name ?? (entity as Asset)?.name;
      case ModelType.LIABILITY:
        return this.liability?.name ?? (entity as Liability)?.name;
      case ModelType.ESTATE_ACCOUNT:
        return this.estateAccount?.user.name ?? (entity as EstateAccountRow)?.user.name;
      default:
        return '';
    }
  }

  resetData(): void {
    this.id = undefined;
    this.file = undefined;
    this.url = undefined;
    this.name = undefined;
  }

  get assignedToText(): string | null {
    const entity: Asset | Liability | EstateAccountRow | null =
      this.asset || this.liability || null;

    return entity
      ? `${entity?.name} - ${entity?.currency.symbol}${entity?.formattedDisplayValue}`
      : null;
  }
}
